// 批次上傳音樂：檔名 → 歌曲 的配對規則（純函式，musicBatchMatch.test.ts 有測）。
//   先比歌曲代號（S02、S06-1…），再比歌名；配不到、一個檔配到好幾首、好幾個檔配同一首，一律不猜，留給人手動選。
//   已經有音樂的歌不能被批次上傳蓋掉（換音樂會刪舊檔，舊檔要留著當比對基準）。

export interface MatchSong {
  id: string;
  name: string;
  code?: string | null;
  /** 這首歌現在有音樂（歌單的 hasMusic / music） */
  hasMusic?: boolean;
}

export type MatchVia = 'code' | 'name';

/** 單看一個檔名的配對結果（還沒看有沒有別的檔也配到同一首） */
export interface FileMatch {
  /** 剛好配到一首 → 那首的 id；否則 null */
  songId: string | null;
  via: MatchVia | null;
  /** 配到好幾首時的候選（給畫面說明）；配不到 = [] */
  candidates: string[];
}

const DASHES = /[‐-―−ー﹘﹣－⁃]/g;

/** 去掉副檔名 */
export function stripExt(name: string): string {
  return name.replace(/\.[A-Za-z0-9]{1,5}$/, '');
}

/** 歌曲代號的比對寫法：全形轉半形、大寫、各種橫線換成 -、去掉空白 */
export function normCode(s: string): string {
  return s.normalize('NFKC').replace(DASHES, '-').toUpperCase().replace(/\s+/g, '');
}

const isAlnum = (ch: string | undefined) => !!ch && /[A-Z0-9]/.test(ch);

/** 檔名裡出現了哪些代號（只回 codes 裡有的）。代號前後不能緊貼英數字；S06 不會配到 S06-1（後面接 -數字就是別的代號） */
export function codesInName(fileName: string, codes: string[]): string[] {
  const text = stripExt(fileName).normalize('NFKC').replace(DASHES, '-').toUpperCase();
  const found: string[] = [];
  const uniq = [...new Set(codes.map(normCode).filter(Boolean))];
  for (const code of uniq) {
    let from = 0;
    while (true) {
      const i = text.indexOf(code, from);
      if (i < 0) break;
      from = i + 1;
      if (isAlnum(text[i - 1])) continue;
      const after = text[i + code.length];
      if (isAlnum(after)) continue;
      if (after === '-' && /[0-9]/.test(text[i + code.length + 1] ?? '')) continue;   // S06-1：不是 S06
      found.push(code);
      break;
    }
  }
  return found;
}

/** 歌名的比對寫法：全形轉半形、小寫、只留文字和數字 */
export function normName(s: string): string {
  return s.normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
}

// 檔名尾巴的版本標記（_v1、-V2、 ver3、v1.2）不算歌名
const VERSION_TAIL = /(?:v|ver|version)\d+(?:\.\d+)*$/;

function nameKeys(fileName: string): string[] {
  const base = stripExt(fileName).normalize('NFKC').toLowerCase();
  const out = new Set<string>();
  const add = (s: string) => {
    const k = normName(s);
    if (k) out.add(k);
    const noVer = k.replace(VERSION_TAIL, '');
    if (noVer) out.add(noVer);
  };
  add(base);


  return [...out];
}

/** 一個檔名配哪一首歌（先代號、再歌名；不猜） */
export function matchFileName(fileName: string, songs: MatchSong[]): FileMatch {
  const withCode = songs.filter(s => s.code && normCode(s.code));
  const hitCodes = codesInName(fileName, withCode.map(s => s.code as string));
  if (hitCodes.length === 1) {
    const hits = withCode.filter(s => normCode(s.code as string) === hitCodes[0]);
    if (hits.length === 1) return { songId: hits[0].id, via: 'code', candidates: [] };
    return { songId: null, via: null, candidates: hits.map(s => s.id) };   // 兩首歌同代號（不該發生）→ 不猜
  }
  if (hitCodes.length > 1) {
    return { songId: null, via: null, candidates: withCode.filter(s => hitCodes.includes(normCode(s.code as string))).map(s => s.id) };
  }
  const keys = nameKeys(fileName);
  const byName = songs.filter(s => { const k = normName(s.name); return !!k && keys.includes(k); });
  if (byName.length === 1) return { songId: byName[0].id, via: 'name', candidates: [] };
  return { songId: null, via: null, candidates: byName.map(s => s.id) };
}

// ─────────────────────────────────────────────
// 每一列的狀態
// ─────────────────────────────────────────────

export type RowPlan =
  | 'ready'         // 可以上傳
  | 'ignored'       // 人選了「不上傳」
  | 'hasMusic'      // 這首歌已經有音樂：跳過，不能勾選
  | 'unmatched'     // 配不到歌
  | 'ambiguous'     // 檔名對得到好幾首歌
  | 'conflict'      // 好幾個檔案要傳給同一首
  | 'badFile';      // 格式 / 大小不收

export interface PlanRow {
  /** 目前選的歌（自動配到或手動選）；null = 沒有 */
  songId: string | null;
  /** 人選了「不上傳」 */
  ignored?: boolean;
  /** 自動配對沒配到時，是因為好幾首都像（true）還是完全沒有（false） */
  ambiguous?: boolean;
  /** 檔案不能傳的說明（格式 / 大小）；null = 沒問題 */
  fileProblem?: string | null;
}

/** 每一列現在是什麼狀態。songs 要是最新的歌單（有沒有音樂以它為準） */
export function planRows(rows: PlanRow[], songs: MatchSong[]): RowPlan[] {
  const byId = new Map(songs.map(s => [s.id, s]));
  const uses = new Map<string, number>();
  for (const r of rows) {
    if (r.ignored || !r.songId || r.fileProblem) continue;
    uses.set(r.songId, (uses.get(r.songId) ?? 0) + 1);
  }
  return rows.map((r): RowPlan => {
    if (r.ignored) return 'ignored';
    if (r.fileProblem) return 'badFile';
    if (!r.songId) return r.ambiguous ? 'ambiguous' : 'unmatched';
    const song = byId.get(r.songId);
    if (!song) return 'unmatched';
    if (song.hasMusic) return 'hasMusic';   // 已有音樂一律跳過：換音樂會刪掉舊檔
    if ((uses.get(r.songId) ?? 0) > 1) return 'conflict';
    return 'ready';
  });
}

/** 這首歌可不可以被批次上傳（已有音樂 = 不行） */
export function songOpenForBatch(song: Pick<MatchSong, 'hasMusic'> | null | undefined): boolean {
  return !!song && !song.hasMusic;
}
